<template>
  <div
    v-if="
      currentMission != null &&
      currentMission.typeName === 'ConstellationOptimizationRequest'
    "
  >
    <v-col cols="7">
      <span class="ml-4 headline grey--text">{{
        currentMission.name.toUpperCase()
      }}</span>
    </v-col>
    <div class="text-xs-center" v-if="displayComputationWidget">
      <exo-mission-computation></exo-mission-computation>
    </div>
    <v-tooltip
      bottom
      class="caption justify-center warning--text"
      v-if="currentMission.dirty"
    >
      <template v-slot:activator="{ on }">
        <v-btn
          large
          class="white--text"
          v-on="on"
          @click.stop="requestComputation()"
          color="warning"
        >
          Start Recompute
        </v-btn>
      </template>
      <span
        >You have changed some components of the mission. This results does not
        match the current inputs. Please recompute the mission to update.</span
      >
    </v-tooltip>
    <v-dialog v-if="displayError" v-model="dialog">
      <template v-slot:activator="{ on: dialog }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on: tooltip }">
            <v-btn
              large
              icon
              class="bug-button"
              v-on="{ ...dialog, ...tooltip }"
            >
              <icon class="white--text" scale="1.5" name="user-injured" />
            </v-btn>
          </template>
          <span>Bug report</span>
        </v-tooltip>
      </template>
      <error-log-display
        :root="_self"
        :computationLog="currentResultsLog"
      ></error-log-display>
    </v-dialog>

    <div v-if="displayResult && !displayError" style="overflow: hidden">
      <v-row v-if="displayDeltaV">
        <v-col cols="12">
          <stats-card
            color="#38AD38"
            icon="ev_station"
            title="Maximum reconfiguration DeltaV"
            cardId="reconfigurationDeltaVMax"
            :value="this.currentResultsLog.results.maximumReconfigurationDeltaV"
            unit="m/s"
          />
        </v-col>
      </v-row>
      <exo-form-alert class="ma-2" type="warning" v-if="impossibleManeuver">
        The computation of the maneuvers deltaVs lead to infeasible maneuvers
        with the defined maneuver duration and spacecraft.
      </exo-form-alert>
      <exo-form-alert class="ma-2" type="info" v-if="optimInterrupted">
        The optimisation has been interrupted to avoid too high computation
        times. Consider decreasing the number of planes and recomputing.
      </exo-form-alert>
      <v-row justify="space-around">
        <v-col cols="5">
          <exo-constellation-optimizer-table
            :items="initialGuessResults"
            title="Initial guess"
            :reference="initialGuessResults"
            :optimized="false"
          ></exo-constellation-optimizer-table>
          <exo-constellation-optimizer-table
            :items="initialGuessPerformanceMetrics"
            title="Initial guess performances"
            :reference="initialGuessPerformanceMetrics"
            :optimized="false"
            class="mt-4"
          ></exo-constellation-optimizer-table>
          <v-card class="mt-4 cesium-card" v-if="!merged">
            <v-card-title class="cesium-card-title">
              <h4>Ground Traces</h4>
              <v-checkbox
                hide-details="true"
                class="ml-6"
                :label="'Merge viewers'"
                v-model="merged"
              />
            </v-card-title>
            <v-divider></v-divider>
            <cesium-viewer
              class="mt-2"
              :pointOfInterest="pointOfInterest"
              :meshesPoints="meshesPoints"
              :meshes="meshesList"
              :tracesResults="{
                initial: tracesInitialResults,
                optimized: undefined
              }"
              :merged="merged"
              name="initial"
            />
          </v-card>
        </v-col>
        <v-col cols="5">
          <exo-constellation-optimizer-table
            v-if="!optimInterrupted"
            :items="optimizationResults"
            title="Optimised constellation"
            :reference="initialGuessResults"
            :optimized="true"
          ></exo-constellation-optimizer-table>
          <exo-constellation-optimizer-table
            v-if="!optimInterrupted"
            :items="optimizationPerformanceMetrics"
            title="Optimised constellation performances"
            :reference="initialGuessPerformanceMetrics"
            :optimized="true"
            class="mt-4"
          ></exo-constellation-optimizer-table>
          <v-card class="mt-4 cesium-card" v-if="!merged">
            <v-card-title class="cesium-card-title">
              <h4>Ground Traces</h4>
              <v-checkbox
                hide-details="true"
                class="ml-6"
                :label="'Merge viewers'"
                v-model="merged"
              />
            </v-card-title>
            <v-divider></v-divider>
            <cesium-viewer
              class="mt-2"
              :pointOfInterest="pointOfInterest"
              :meshesPoints="meshesPoints"
              :meshes="meshesList"
              :tracesResults="{
                initial: undefined,
                optimal: tracesOptimizedResults
              }"
              :merged="merged"
              name="optimal"
            />
          </v-card>
        </v-col>
      </v-row>
      <v-row justify="space-around" v-if="merged">
        <v-col cols="11">
          <v-card class="cesium-card" style="height: 90vh">
            <v-card-title class="cesium-card-title">
              <h4>Ground Traces</h4>
              <v-checkbox
                hide-details="true"
                class="ml-6"
                :label="'Merge viewers'"
                v-model="merged"
              />
            </v-card-title>
            <v-divider></v-divider>
            <cesium-viewer
              class="mt-2"
              :pointOfInterest="pointOfInterest"
              :meshesPoints="meshesPoints"
              :meshes="meshesList"
              :tracesResults="{
                initial: tracesInitialResults,
                optimal: tracesOptimizedResults
              }"
              :merged="true"
            />
          </v-card>
        </v-col>
      </v-row>
      <v-row class="mt-4 ml-15">
        <v-col cols="10">
          <v-row v-if="!optimInterrupted">
            <v-text-field
              class="ml-3"
              id="inputName"
              label="Name"
              v-model="name"
              maxlength="100"
              :rules="[inputNameFeedback]"
            ></v-text-field>
            <v-btn
              class="mt-3"
              color="primary"
              :disabled="inputNameState"
              v-on:click="saveOptimizedConstellation()"
              >Save</v-btn
            >
          </v-row>
          <v-row v-if="this.savedConstellation" no-gutters>
            <v-btn
              class="mt-3 ml-3 white--text"
              color="red"
              :disabled="false"
              v-on:click="requestPerformancesMission()"
              >To Performances</v-btn
            >
            <v-btn
              class="mt-3 ml-3 white--text"
              color="blue"
              :disabled="false"
              v-on:click="requestNumericalSimulation()"
              >To Simulation</v-btn
            >
          </v-row>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import dashboardComponents from '../../components/Dashboard/index.js'
import { mapGetters } from 'vuex'
import Vue from 'vue'
import errorMixin from '../../mixins/logHandler.js'
import constellationUtils from '../../mixins/constellationUtils'
import 'cesium/Widgets/widgets.css'
import dataDescription from '../../tools/dataDescription.json'
import dataTools from '../../tools/data'
import dashboardTools from '../../tools/dashboard'

import DashboardGroundTracksViewer from './DashboardGroundTracksViewer.vue'

Vue.use(dashboardComponents)
export default {
  name: 'ConstellationDashboard',
  mixins: [errorMixin, constellationUtils],

  components: {
    'cesium-viewer': DashboardGroundTracksViewer
  },

  mounted() {
    this.computeEphemerisTraces()
    if (this.displayResult === true) {
      this.displayDataTables()
    }
  },

  beforeDestroy() {
    this.currentData = null
    this.emptyResult = false
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      currentMission: 'currentMission',
      currentResultsLog: 'currentResultsLog'
    }),
    traceBuilderWatcher() {
      return [
        this.initialTracesRoot.inputs.visualizationDate,
        this.optimalTracesRoot.inputs.visualizationDate,
        this.optimizationResults
      ]
    },
    displayComputationWidget() {
      return dashboardTools.displayComputationWidget(this)
    },
    displayResult() {
      return dashboardTools.displayResult(this)
    },
    displayDeltaV() {
      return !(
        this.currentResultsLog.results.maximumReconfigurationDeltaV === 'NaN'
      )
    },
    impossibleManeuver() {
      return (
        !this.displayDeltaV &&
        this.currentResultsLog.results.reconfigurationDeltaVNeeded
      )
    },
    displayError() {
      return dashboardTools.displayError(this)
    },
    computationNeeded() {
      return (
        this.currentMission &&
        this.currentMission.computationRequested &&
        this.currentMission.computationLogId === ''
      )
    },
    dataLoaded() {
      return dashboardTools.dataLoaded(this)
    },
    dataReady() {
      return this.dataLoaded && this.currentData != null && !this.emptyResult
    },

    loading() {
      return this.currentData == null && this.dataLoaded
    },

    resultLoaded() {
      return (
        this.currentMission &&
        this.currentResultsLog != null &&
        this.currentResultsLog.results != null &&
        this.currentResultsLog.errors != null
      )
    },

    optimInterrupted() {
      return this.currentResultsLog?.results?.optimizationInterrupted === true
    },

    inputNameFeedback() {
      let feedback = true
      if (this.inputNameState !== null && this.inputNameState) {
        feedback =
          "Please only use the allowed characters : a-z A-Z 0-9 ' ',.-+=/_#@$&:%|()]["
      }
      return feedback
    },

    inputNameState() {
      let valid = null
      if (this.name !== '') {
        let allowedChars = RegExp(
          '^[a-zA-Z0-9 ,\\.\\-\\+=\\/_@#\\$&:%\\|\\(\\)\\[\\]]+$'
        )
        valid = allowedChars.test(this.name)
      }
      return !valid
    }
  },

  data() {
    return {
      optimizationResults: null,
      initialGuessResults: null,
      optimizationPerformanceMetrics: null,
      initialGuessPerformanceMetrics: null,
      name: '',
      dialog: false,

      savedConstellation: false,
      savedConstellationObject: null,

      pointOfInterest: null,
      meshesList: [],
      meshesPoints: [],

      tracesInitialResults: {},
      tracesOptimizedResults: {},

      errorTrace: '',

      initialTracesRoot: {
        inputs: {
          visualizationDate: '2022-01-01T12:00:00.000Z'
        }
      },
      optimalTracesRoot: {
        inputs: {
          visualizationDate: '2022-01-01T12:00:00.000Z'
        }
      },
      merged: false,
      context: 'ConstellationOptimizationResult'
    }
  },

  watch: {
    displayResult() {
      this.displayDataTables()
    },

    traceBuilderWatcher: function (value) {
      setTimeout(() => {
        this.computeEphemerisTraces()
      }, 200)
    }
  },

  methods: {
    displayDataTables() {
      if (
        this.currentResultsLog?.results?.optimizationInterrupted === false &&
        this.currentResultsLog?.results?.constellationOptimizationResult
          ?.computedConstellation?.computedHomogeneousConstellationGeometries !=
          null
      ) {
        this.optimizationResults = this.getConstellationMarker(
          this.currentResultsLog.results.constellationOptimizationResult
            .computedConstellation
        )
        this.optimizationPerformanceMetrics =
          this.getConstellationPerformanceMetrics(
            this.currentResultsLog.results.constellationOptimizationResult
              .performanceMetrics
          )
      }
      if (
        this.currentResultsLog?.results?.initialConstellationResult
          ?.computedConstellation != null
      ) {
        this.initialGuessResults = this.getConstellationMarker(
          this.currentResultsLog.results.initialConstellationResult
            .computedConstellation
        )
        this.initialGuessPerformanceMetrics =
          this.getConstellationPerformanceMetrics(
            this.currentResultsLog.results.initialConstellationResult
              .performanceMetrics
          )
      }
    },

    getAPI(objectAPI) {
      return '/users/' + this.currentUser.id + '/' + objectAPI.toLowerCase()
    },

    async computeEarthMesh(meshFormObject) {
      let meshPoints = await this.$http
        .post('/physics/getearthmeshcoordinates', meshFormObject)
        .then(function (response) {
          return response.data
        })
        .catch((e, __) => {
          this.errorTrace = this.getErrorMessage(e)
          return null
        })
      return meshPoints
    },

    async computeEphemerisTraces() {
      if (
        this.currentResultsLog?.results?.initialConstellationResult &&
        this.currentResultsLog?.results?.constellationOptimizationResult
      ) {
        this.pointOfInterest =
          this.currentResultsLog.results.computedZoneOfInterest.isolatedPointsOfInterest
        this.earthMeshes =
          this.currentResultsLog.results.computedZoneOfInterest.earthMeshes
        let meshesPoints = []
        if (this.earthMeshes && this.earthMeshes.length > 0) {
          this.earthMeshes.forEach(async (meshFormObject) => {
            this.computeEarthMesh(meshFormObject).then((points) => {
              meshesPoints.push(points)
            })
          })
          this.meshesPoints = meshesPoints
          this.meshesList = this.earthMeshes
        }
        let initialResult =
          this.currentResultsLog.results.initialConstellationResult
        let optimalResult =
          this.currentResultsLog.results.constellationOptimizationResult
        // Initial results
        if (
          initialResult.computedConstellation
            ?.computedHomogeneousConstellationGeometries
        ) {
          let constellationGeometries =
            initialResult.computedConstellation
              .computedHomogeneousConstellationGeometries
          let traces = []
          let payloads = []
          for (let geometry of constellationGeometries) {
            if (!geometry.visualizationDate) {
              if (!this.initialTracesRoot.inputs.visualizationDate) {
                geometry.visualizationDate =
                  this.initialTracesRoot.inputs.visualizationDate
              } else {
                geometry.visualizationDate = '2022-01-01T12:00:00.000Z'
              }
            }

            let data = await this.$http
              .post('/physics/computedconstellationorbittraces', geometry)
              .then(function (response) {
                return response.data
              })
              .catch((e) => {
                this.errorTrace = this.getErrorMessage(e)
                return null
              })
            if (data == null) {
              traces = null
            } else {
              this.errorTrace = ''
              traces = traces.concat([data.ephemeridesTraces])
              payloads = payloads.concat(data?.payload?.fieldOfView)
            }
          }
          if (traces == null) {
            this.tracesInitialResults = null
          } else {
            let result = {
              traces: traces,
              payloads: payloads
            }
            this.tracesInitialResults = result
          }
        }
        // Optimal results
        if (
          optimalResult.computedConstellation
            ?.computedHomogeneousConstellationGeometries
        ) {
          let constellationGeometries =
            optimalResult.computedConstellation
              .computedHomogeneousConstellationGeometries
          let traces = []
          let payloads = []
          for (let geometry of constellationGeometries) {
            if (!geometry.visualizationDate) {
              if (!this.optimalTracesRoot.inputs.visualizationDate) {
                geometry.visualizationDate =
                  this.optimalTracesRoot.inputs.visualizationDate
              } else {
                geometry.visualizationDate = '2022-01-01T12:00:00.000Z'
              }
            }

            let data = await this.$http
              .post('/physics/computedconstellationorbittraces', geometry)
              .then(function (response) {
                return response.data
              })
              .catch((e) => {
                this.errorTrace = this.getErrorMessage(e)
                return null
              })
            if (data == null) {
              traces = null
            } else {
              this.errorTrace = ''
              traces = traces.concat([data.ephemeridesTraces])
              payloads = payloads.concat(data?.payload?.fieldOfView)
            }
          }
          if (traces == null) {
            this.tracesOptimizedResults = null
          } else {
            let result = {
              traces: traces,
              payloads: payloads
            }
            this.tracesOptimizedResults = result
          }
        }
      }
    },

    getDisplayedValueWithUnit(results, field) {
      let value = results
      if (results == null) {
        return ''
      }
      let type = dataTools.getFieldTypeFromPath(field, this.context, null)
      for (let i = 0; i < field.length; i++) {
        value = value[field[i]]
        if (value == null) {
          return 'undefined'
        }
      }
      if (value == null || isNaN(value) || value === 'NaN') {
        return 'undefined'
      } else {
        value = dataTools.convertSi2Display(type, value)
      }
      return (
        value +
        ' ' +
        dataTools.getUnitDisplayed(
          dataTools.getDefaultGuiUnit(
            dataTools.getFieldTypeFromPath(field, this.context, null)
          )
        )
      )
    },

    buildResultFieldValue(results, field) {
      let type = dataTools.getFieldTypeFromPath(field, this.context, null)
      if (
        dataDescription[type] != null &&
        (dataDescription[type].type === 'int' ||
          dataDescription[type].type === 'double' ||
          dataDescription[type].type === 'integer')
      ) {
        return this.getDisplayedValueWithUnit(results, field)
      } else if (field[0] === 'initialRaans') {
        return this.getDisplayedAngleList(results, field)
      } else {
        return this.getDisplayedString(results, field)
      }
    },

    buildResultCustomFieldValue(results, field) {
      let stringToDisplay = ''
      switch (field[0]) {
        case 'altitudeRange':
          return stringToDisplay + this.computeAltitudeRange(results)
        case 'inclinationRange':
          return stringToDisplay + this.computeInclinationRange(results)
        case 'interPlanePhasingRange':
          return stringToDisplay + this.computeInterPlanePhasingRange(results)
        case 'numberOfPlanes':
          return stringToDisplay + this.computeNumberOfPlanes(results)
        case 'numberOfSpacecrafts':
          return stringToDisplay + this.computeNumberOfSpacecrafts(results)
        case 'Raans':
          return stringToDisplay + this.computeRaans(results)
        default:
          return 'undefined'
      }
    },

    computeNumberOfPlanes(data) {
      let geometries = data.computedHomogeneousConstellationGeometries
      let numberOfPlanes = 0
      for (let geometry of geometries) {
        numberOfPlanes = numberOfPlanes + geometry.numberOfPlanes
      }
      return numberOfPlanes
    },

    computeNumberOfSpacecrafts(data) {
      let geometries = data.computedHomogeneousConstellationGeometries
      let numberOfSpacecrafts = 0
      for (let geometry of geometries) {
        numberOfSpacecrafts =
          numberOfSpacecrafts +
          geometry.numberOfPlanes * geometry.numberOfSpacecraftsPerPlane
      }
      return numberOfSpacecrafts
    },

    computeAltitudeRange(data) {
      let geometries = data.computedHomogeneousConstellationGeometries
      let minAltitude = 10000000000
      let maxAltitude = 0
      for (let geometry of geometries) {
        let currentAltitude = geometry.plane.altitude
        if (currentAltitude < minAltitude) {
          minAltitude = Math.round(currentAltitude * 0.001 * 1e3) / 1e3
        }
        if (currentAltitude > maxAltitude) {
          maxAltitude = Math.round(currentAltitude * 0.001 * 1e3) / 1e3
        }
      }
      if (minAltitude === maxAltitude) {
        return minAltitude
      }
      if (minAltitude === 10000000000 && maxAltitude === 0) {
        return 'undefined'
      } else {
        return '[' + minAltitude + ', ' + maxAltitude + ']'
      }
    },

    computeInclinationRange(data) {
      let geometries = data.computedHomogeneousConstellationGeometries
      let minInclination = 181
      let maxInclination = 0
      for (let geometry of geometries) {
        let currentInclination = dataTools.rad2deg(geometry.plane.inclination)
        if (currentInclination < minInclination) {
          minInclination = Math.round(currentInclination * 1e2) / 1e2
        }
        if (currentInclination > maxInclination) {
          maxInclination = Math.round(currentInclination * 1e2) / 1e2
        }
      }
      if (minInclination === maxInclination) {
        return minInclination
      }
      if (minInclination === 181 && maxInclination === 0) {
        return 'undefined'
      } else {
        return '[' + minInclination + ', ' + maxInclination + ']'
      }
    },

    computeInterPlanePhasingRange(data) {
      let geometries = data.computedHomogeneousConstellationGeometries
      let minInterPlanePhasing = 181
      let maxInterPlanePhasing = -181
      for (let geometry of geometries) {
        let currentInterPlanePhasing = dataTools.rad2deg(
          geometry.interPlanePhasing
        )
        if (currentInterPlanePhasing < minInterPlanePhasing) {
          minInterPlanePhasing =
            Math.round(currentInterPlanePhasing * 1e2) / 1e2
        }
        if (currentInterPlanePhasing > maxInterPlanePhasing) {
          maxInterPlanePhasing =
            Math.round(currentInterPlanePhasing * 1e2) / 1e2
        }
      }
      if (minInterPlanePhasing === maxInterPlanePhasing) {
        return minInterPlanePhasing
      }
      if (minInterPlanePhasing === 181 && maxInterPlanePhasing === 0) {
        return 'undefined'
      } else {
        return '[' + minInterPlanePhasing + ', ' + maxInterPlanePhasing + ']'
      }
    },

    computeRaans(data) {
      let geometries = data.computedHomogeneousConstellationGeometries
      let raans = ''
      for (let geometry of geometries) {
        let currentRaans = geometry.initialRaans
        for (let raan of currentRaans) {
          raans += Math.round(dataTools.rad2deg(raan) * 1e2) / 1e2 + '; '
        }
        raans += ' / '
      }
      return raans.substring(0, raans.length - 5)
    },

    getDisplayedString(results, field) {
      let value = results
      if (value == null) {
        return ''
      }
      for (let i = 0; i < field.length; i++) {
        if (value == null) {
          return ''
        }
        value = value[field[i]]
      }
      if (value == null) {
        return ''
      }
      return (
        value +
        ' ' +
        dataTools.getUnitDisplayed(
          dataTools.getDefaultGuiUnit(
            dataTools.getFieldTypeFromPath(field, this.context, null)
          )
        )
      )
    },

    getDisplayedAngleList(results, field) {
      let values = results[field[0]]
      let stringToDisplay = ''
      if (!Array.isArray(values) || !values.length) {
        return ''
      }
      for (let value of values) {
        /// TODO convertrad/deg dataTools.deg2rad(
        let valueInDegres = Math.round(dataTools.rad2deg(value) * 1e2) / 1e2
        stringToDisplay += valueInDegres + '; '
      }
      stringToDisplay =
        stringToDisplay.substring(0, stringToDisplay.length - 2) + ' (deg)'
      return stringToDisplay
    },

    getConstellationMarker(constellation) {
      return [
        {
          name: 'Altitude range (km)',
          value: this.buildResultCustomFieldValue(constellation, [
            'altitudeRange'
          ])
        },
        {
          name: 'Inclination range (deg)',
          value: this.buildResultCustomFieldValue(constellation, [
            'inclinationRange'
          ])
        },
        {
          name: 'Inter-plane phasing range (deg)',
          value: this.buildResultCustomFieldValue(constellation, [
            'interPlanePhasingRange'
          ])
        },
        {
          name: 'Number of planes',
          value: this.buildResultCustomFieldValue(constellation, [
            'numberOfPlanes'
          ])
        },
        {
          name: 'Number of Spacecrafts',
          value: this.buildResultCustomFieldValue(constellation, [
            'numberOfSpacecrafts'
          ])
        },
        {
          name: 'RAANs (deg)',
          value: this.buildResultCustomFieldValue(constellation, ['Raans'])
        }
      ]
    },
    getConstellationPerformanceMetrics(performanceMetrics) {
      if (performanceMetrics.numberOfSeenPoints === 0) {
        return [
          {
            name: 'Number of seen points',
            value: this.buildResultFieldValue(performanceMetrics, [
              'numberOfSeenPoints'
            ])
          }
        ]
      } else {
        return [
          {
            name: 'Average revisit time',
            value: this.buildResultFieldValue(performanceMetrics, [
              'averagePointRevisitTime'
            ])
          },
          {
            name: 'Max revisit time',
            value: this.buildResultFieldValue(performanceMetrics, [
              'maxPointRevisitTime'
            ])
          },
          {
            name: 'Percentage of seen points',
            value: this.buildResultFieldValue(performanceMetrics, [
              'percentageOfSeenPoints'
            ])
          }
        ]
      }
    },
    requestComputation() {
      this.$router.push({ path: '/constellationrevisitoptimizationmission' })
      Vue.nextTick(() => {
        // use nextTick to be sure mission components are created and can handle events
        this.$root.$emit('request-computation-constellation-optimization')
      })
    },
    async saveOptimizedConstellation() {
      // TODO WIP
      let computedConstellation =
        this.currentResultsLog.results.constellationOptimizationResult
          .computedConstellation
      computedConstellation.name = this.name
      let i = 1
      for (let geometry of computedConstellation.computedHomogeneousConstellationGeometries) {
        geometry.name = this.name + '(' + i + ')'
        i = i + 1
      }
      try {
        this.savedConstellationObject = (
          await this.$http.post(
            this.getAPI(`constellationdefinitions/fromresult`),
            computedConstellation
          )
        ).data
        this.displayMessage(
          'Constellation saved as :' + this.name,
          '',
          'notification',
          'success',
          1500
        )
        this.savedConstellation = true
      } catch (e) {
        this.displayMessage(
          'Constellation save failed.',
          e,
          'notification',
          'error'
        )
      }
    },
    requestPerformancesMission() {
      const mission = {
        constellation: this.savedConstellationObject,
        typeName: 'ConstellationPerformanceAnalysisRequest',
        simulationDuration: dataTools.convertUnits(
          this.currentResultsLog?.results?.constellationOptimizationResult
            .simulationDuration,
          's',
          'days'
        )
      }
      this.$router.push({ path: '/analyticalconstellationsimulation' })
      this.sendMissionEvent('create-mission', mission)
    },

    requestNumericalSimulation() {
      const mission = {
        constellation: this.savedConstellationObject,
        typeName: 'ConstellationSimulationRequest',
        simulationDuration: dataTools.convertUnits(
          this.currentResultsLog?.results?.constellationOptimizationResult
            .simulationDuration,
          's',
          'days'
        )
      }
      this.$router.push({ path: '/numericalconstellationmission' })
      this.sendMissionEvent('create-mission', mission)
    },

    sendMissionEvent(eventName, mission) {
      // event send to root because it should be treated by sub components
      // use nextTick to be sure mission components are created and can handle events
      Vue.nextTick(() => {
        this.$root.$emit(eventName, mission)
      })
    }
  }
}
</script>

<style scoped>
.cesium-card {
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: 8% 1% 91%;
  height: 60vh;
  transition: all 0.3s linear;
}

.cesium-card-title {
  display: inline-flex;
  align-items: center;
}

.cesium-card-title .v-input,
.cesium-card-title .v-input__slot {
  margin: 0;
}

.cesium-card-title .v-messages {
  display: none !important;
}

.cesiumColorScale {
  position: absolute;
  left: 1%;
  top: 1%;
  z-index: 2;
  color: #fff;
}

.bug-button {
  background: #ff5252 !important;
}
</style>
